import { useState } from "react";
import { GitBranch } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Card, EmptyState, Field, NativeSelect, Skeleton } from "@ncp/ui";
import { useGraph, useMarkets } from "./api";
import { MarketGraphSvg } from "./MarketGraphView";

/** Mode 3 — the ecosystem graph of a market: owners, directors, principals and agencies around the undertakings, with the structural findings. */
export function GraphTab() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const markets = useMarkets();
  const withShares = (markets.data ?? []).filter((m) => m.entities > 0).sort((a, b) => (b.hhi ?? 0) - (a.hhi ?? 0));
  const [selected, setSelected] = useState<string>("");
  const marketId = selected || withShares[0]?.id || null;
  const graph = useGraph(marketId);
  const g = graph.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="max-w-3xl text-sm text-muted-foreground">{t("backoffice:dataBank.intel.graphHint")}</p>
        <Field label={t("backoffice:dataBank.market")} className="w-full max-w-xl">{(id) => <NativeSelect id={id} value={marketId ?? ""} onChange={(e) => setSelected(e.target.value)}>{withShares.map((m) => <option key={m.id} value={m.id}>{locale === "ar" ? m.nameAr : m.nameEn}</option>)}</NativeSelect>}</Field>
      </div>
      {graph.isLoading ? <Skeleton className="h-96" /> : !g || g.nodes.length <= 1 ? <EmptyState icon={GitBranch} title={t("backoffice:dataBank.intel.graphEmpty")} description={t("backoffice:dataBank.intel.graphEmptyHint")} /> : (
        <>
          <Card className="p-6">
            <MarketGraphSvg graph={g} />
            <p className="mt-2 text-[11px] text-muted-foreground">{t("backoffice:dataBank.intel.graphLegend")}</p>
          </Card>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-6">
              <h4 className="mb-3 text-base font-semibold text-aeblack-950">{t("backoffice:dataBank.intel.structuralFindings")}</h4>
              {g.findings.length === 0 ? <p className="text-sm text-muted-foreground">{t("backoffice:dataBank.intel.noFindings")}</p> : (
                <ul className="divide-y divide-sand-100">
                  {g.findings.map((f) => (
                    <li key={f.title} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                      <Badge variant={f.points >= 25 ? "danger" : "warning"} size="sm" className="shrink-0">{t(`backoffice:dataBank.intel.findingKind.${f.kind}`, { defaultValue: f.kind })}</Badge>
                      <div><p className="text-sm font-medium text-aeblack-950">{locale === "ar" ? f.titleAr ?? f.title : f.title}</p><p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{locale === "ar" ? f.detailAr ?? f.detail : f.detail}</p></div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card className="p-6">
              <h4 className="mb-3 text-base font-semibold text-aeblack-950">{t("backoffice:dataBank.intel.agencies")}</h4>
              {g.agencies.length === 0 ? <p className="text-sm text-muted-foreground">{t("backoffice:dataBank.intel.noAgencies")}</p> : (
                <ul className="divide-y divide-sand-100 text-xs">
                  {g.agencies.map((a) => (
                    <li key={`${a.agent}-${a.principal}`} className="grid gap-1 py-2.5 first:pt-0 last:pb-0">
                      <p className="text-sm font-medium text-aeblack-950">{a.agent} <span className="text-muted-foreground">←</span> {a.principal}{a.principalCountry ? ` (${a.principalCountry})` : ""}</p>
                      <p className="text-muted-foreground">{a.products} · {a.territory} · {a.exclusive ? t("backoffice:dataBank.intel.exclusive") : t("backoffice:dataBank.intel.nonExclusive")}{a.expiresOn ? ` · ${t("backoffice:dataBank.intel.until")} ${a.expiresOn}` : ""}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
